import { Link } from "react-router-dom";
import { useEffect } from "react";
import { ArrowLeft, AlertTriangle } from "lucide-react";
import { WingedM } from "@/components/mazaro/Logo";

export function H2({ children }) {
  return <h2 className="font-display font-extrabold uppercase tracking-tight text-xl sm:text-2xl text-white mt-10 mb-3">{children}</h2>;
}

export function H3({ children }) {
  return <h3 className="font-display font-bold uppercase tracking-tight text-lg text-gold mt-8 mb-2">{children}</h3>;
}

export function P({ children }) {
  return <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">{children}</p>;
}

export function UL({ items }) {
  return (
    <ul className="list-disc pl-5 space-y-1.5 text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
      {items.map((it, i) => (
        <li key={i}>{typeof it === "string" ? <span>{it}</span> : it}</li>
      ))}
    </ul>
  );
}

export function PH({ children }) {
  return (
    <div className="border border-dashed border-gold/50 bg-gold/5 rounded-md px-4 py-3 my-4 text-sm text-goldlight leading-relaxed">
      <strong className="text-gold font-mono text-[10px] tracking-[0.2em] uppercase block mb-1">Platzhalter — ausfüllen</strong>
      {children}
    </div>
  );
}

const LEGAL_PAGES = [
  ["/impressum", "Impressum"],
  ["/datenschutz", "Datenschutz"],
  ["/agb", "AGB"],
  ["/widerruf", "Widerrufsbelehrung"],
  ["/widerrufsformular", "Widerrufsformular"],
  ["/versand", "Versand"],
  ["/rueckgabe", "Rückgabe & Erstattung"],
  ["/kontakt", "Kontakt"],
];

export default function LegalLayout({ title, children }) {
  useEffect(() => {
    document.title = `${title} — MAZARO`;
  }, [title]);

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="legal-page">
      <div className="grain" aria-hidden="true" />
      <header className="backdrop-blur-xl bg-black/70 border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="legal-home-link" className="flex items-center gap-3 hover:opacity-80">
            <WingedM size={38} />
            <span className="font-display font-black uppercase tracking-[0.2em] text-lg">Mazaro</span>
          </Link>
          <Link to="/" data-testid="legal-back-button" className="btn-ghost !px-4 !py-2">
            <ArrowLeft size={14} />
            Zurück
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-14 relative z-10">
        <div className="border border-gold/30 bg-gold/5 rounded-md px-5 py-4 mb-10 flex gap-3" data-testid="legal-template-warning">
          <AlertTriangle size={18} className="text-gold shrink-0 mt-0.5" />
          <p className="text-xs sm:text-sm text-goldlight leading-relaxed">
            <strong>Vorlagen-Hinweis:</strong> Diese Rechtstexte sind Vorlagen. Alles in{" "}
            <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-gold">Platzhalter-Kästen</span> muss vor
            dem verbindlichen Veröffentlichen mit echten Angaben ausgefüllt und rechtlich geprüft werden. Diese Seite ist
            keine Rechtsberatung.
          </p>
        </div>

        <h1 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl mb-2">{title}</h1>
        <div className="h-[3px] w-20 bg-gold mb-8" />

        {children}

        <div className="mt-16 border-t border-white/10 pt-8">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-4">Rechtliches</p>
          <div className="flex flex-wrap gap-2" data-testid="legal-nav">
            {LEGAL_PAGES.map(([href, label]) => (
              <Link
                key={href}
                to={href}
                className="font-mono text-[11px] tracking-[0.15em] uppercase px-4 py-2 border border-white/15 text-slate-300 hover:border-gold/60 hover:text-white transition-colors"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
